import { useEffect, useRef } from "react";

/**
 * A native modal <dialog>: the browser keeps focus inside it, makes the page behind inert and
 * turns Escape into `onClose`. Clicking the backdrop closes it too. Focus returns to whatever was
 * focused before it opened.
 */
export function Modal({ label, className, onClose, children }: { label: string; className: string; onClose: () => void; children: React.ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const latest = useRef(onClose);
  latest.current = onClose;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const before = document.activeElement as HTMLElement | null;
    if (typeof el.showModal === "function") el.showModal();
    else el.setAttribute("open", "");
    // The browser focuses the first focusable element; prefer one marked data-autofocus, then the first text field.
    el.querySelector<HTMLElement>("[data-autofocus], input:not([type=checkbox]), textarea")?.focus();
    return () => {
      // Close first: while the dialog is still modal, everything behind it is inert and can't take focus.
      if (el.open) el.close?.();
      before?.focus?.();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className={`modal ${className}`}
      aria-label={label}
      onCancel={(e) => {
        e.preventDefault();
        latest.current();
      }}
      onMouseDown={(e) => {
        // A press on the dialog element itself outside its box is a press on the backdrop.
        if (e.target !== e.currentTarget) return;
        const r = e.currentTarget.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) latest.current();
      }}
    >
      {children}
    </dialog>
  );
}
